// Shared helpers for keeping intake form_data aligned with the backend schema.

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormDataRecord = Record<string, any>;

const INTAKE_BOOLEAN_PATH_PATTERNS = [
  /^incident_info\.(ems|currently_treating|police_report|other_driver_ticket|other_driver_fault|owner_knew_about_hazard|security_cameras|similar_incidents|filed_complaint|photos)$/,
  /^medical_wage_history\.(has_lost_wages|prior_injuries)$/,
  /^medical_wage_history\.treatments\.\d+\.still_treating$/,
];

export const isIntakeBooleanPath = (path: string): boolean =>
  INTAKE_BOOLEAN_PATH_PATTERNS.some((pattern) => pattern.test(path));

export const coerceBooleanFieldValue = (value: unknown): boolean =>
  value === true ||
  value === 'true' ||
  value === 'yes' ||
  value === 1 ||
  value === '1';

const hasOwn = (value: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

// Keep merge semantics aligned with intake form PATCH behavior:
// nested objects merge recursively, treatment rows merge by index, and an
// explicit empty array replaces the previous value entirely.
const mergeFormData = (
  target: FormDataRecord,
  source: FormDataRecord,
): FormDataRecord => {
  const result = { ...target };

  Object.keys(source).forEach((key) => {
    const sourceValue = source[key];
    const targetValue = result[key];

    if (Array.isArray(sourceValue)) {
      if (!Array.isArray(targetValue) || sourceValue.length === 0) {
        result[key] = sourceValue;
        return;
      }

      const merged = [...targetValue];
      sourceValue.forEach((item, index) => {
        if (
          item &&
          typeof item === 'object' &&
          !Array.isArray(item) &&
          merged[index] &&
          typeof merged[index] === 'object' &&
          !Array.isArray(merged[index])
        ) {
          merged[index] = mergeFormData(
            merged[index] as FormDataRecord,
            item as FormDataRecord,
          );
          return;
        }

        if (item !== undefined) {
          merged[index] = item;
        }
      });

      result[key] = merged;
      return;
    }

    if (
      sourceValue &&
      typeof sourceValue === 'object' &&
      !Array.isArray(sourceValue) &&
      targetValue &&
      typeof targetValue === 'object' &&
      !Array.isArray(targetValue)
    ) {
      result[key] = mergeFormData(
        targetValue as FormDataRecord,
        sourceValue as FormDataRecord,
      );
      return;
    }

    if (sourceValue !== undefined) {
      result[key] = sourceValue;
    }
  });

  return result;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sanitizeTreatmentRow = (treatment: any, repairMissingField: boolean) => {
  if (!treatment || typeof treatment !== 'object') {
    return treatment;
  }

  if (
    !repairMissingField &&
    !hasOwn(treatment as Record<string, unknown>, 'still_treating')
  ) {
    return treatment;
  }

  if (typeof treatment.still_treating === 'boolean') {
    return treatment;
  }

  return {
    ...treatment,
    still_treating: coerceBooleanFieldValue(treatment.still_treating),
  };
};

export const sanitizeIntakeFormData = <T extends FormDataRecord>(data: T): T => {
  let result = data;

  if (
    result?.personal_info &&
    typeof result.personal_info.emergency_contact === 'string'
  ) {
    result = {
      ...result,
      personal_info: {
        ...result.personal_info,
        emergency_contact: { name: result.personal_info.emergency_contact },
      },
    };
  }

  if (
    result?.medical_wage_history &&
    typeof result.medical_wage_history.treatments === 'string'
  ) {
    result = {
      ...result,
      medical_wage_history: {
        ...result.medical_wage_history,
        treatments: [],
      },
    };
  }

  if (!Array.isArray(result?.medical_wage_history?.treatments)) {
    return result;
  }

  let hasUpdatedTreatment = false;
  const sanitizedTreatments = result.medical_wage_history.treatments.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (treatment: any) => {
      const sanitizedTreatment = sanitizeTreatmentRow(treatment, false);
      if (sanitizedTreatment !== treatment) {
        hasUpdatedTreatment = true;
      }
      return sanitizedTreatment;
    },
  );

  if (!hasUpdatedTreatment) {
    return result;
  }

  return {
    ...result,
    medical_wage_history: {
      ...result.medical_wage_history,
      treatments: sanitizedTreatments,
    },
  };
};

export const buildSanitizedRepairPatch = (
  data: FormDataRecord | null | undefined,
): FormDataRecord => {
  const patch: FormDataRecord = {};
  const source = data ?? {};

  if (
    source.personal_info &&
    typeof source.personal_info.emergency_contact === 'string'
  ) {
    patch.personal_info = {
      ...(patch.personal_info ?? {}),
      emergency_contact: { name: source.personal_info.emergency_contact },
    };
  }

  if (!source.medical_wage_history) {
    return patch;
  }

  if (typeof source.medical_wage_history.treatments === 'string') {
    patch.medical_wage_history = {
      ...(patch.medical_wage_history ?? {}),
      treatments: [],
    };
    return patch;
  }

  if (!Array.isArray(source.medical_wage_history.treatments)) {
    return patch;
  }

  const repairedTreatments = source.medical_wage_history.treatments.map(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (treatment: any) => sanitizeTreatmentRow(treatment, true),
  );

  const hasTreatmentRepair = repairedTreatments.some(
    (treatment: unknown, index: number) =>
      treatment !== source.medical_wage_history.treatments[index],
  );

  if (!hasTreatmentRepair) {
    return patch;
  }

  patch.medical_wage_history = {
    ...(patch.medical_wage_history ?? {}),
    treatments: repairedTreatments,
  };

  return patch;
};

export const mergeFormDataPatchWithRepairs = (
  rawFormData: FormDataRecord | null | undefined,
  patch: FormDataRecord,
): FormDataRecord => {
  const repairPatch = buildSanitizedRepairPatch(rawFormData);
  const sanitizedPatch = sanitizeIntakeFormData(patch);
  return mergeFormData(repairPatch, sanitizedPatch);
};

const PYTHON_STYLE_STILL_TREATING_PATTERN =
  /\('medical_wage_history', 'treatments', (\d+), 'still_treating'\)/g;
const JSON_STYLE_STILL_TREATING_PATTERN =
  /"medical_wage_history"\s*,\s*"treatments"\s*,\s*(\d+)\s*,\s*"still_treating"/g;

const collectMatchedIndexes = (pattern: RegExp, value: string): number[] => {
  const indexes: number[] = [];
  pattern.lastIndex = 0;
  let match = pattern.exec(value);

  while (match) {
    const index = Number.parseInt(match[1], 10);
    if (!Number.isNaN(index)) {
      indexes.push(index);
    }
    match = pattern.exec(value);
  }

  return indexes;
};

export const buildStillTreatingRepairPatchFromValidationError = (
  errorData: unknown,
): FormDataRecord | null => {
  if (!errorData || typeof errorData !== 'object') {
    return null;
  }

  const data = errorData as { error?: unknown; details?: unknown };
  if (data.error !== 'validation error') {
    return null;
  }

  const details =
    typeof data.details === 'string'
      ? data.details
      : JSON.stringify(data.details ?? '');

  const indexes = Array.from(
    new Set([
      ...collectMatchedIndexes(
        PYTHON_STYLE_STILL_TREATING_PATTERN,
        details,
      ),
      ...collectMatchedIndexes(JSON_STYLE_STILL_TREATING_PATTERN, details),
    ]),
  ).sort((left, right) => left - right);

  if (indexes.length === 0) {
    return null;
  }

  const matchedIndexes = new Set(indexes);
  const maxIndex = indexes[indexes.length - 1];

  return {
    medical_wage_history: {
      treatments: Array.from({ length: maxIndex + 1 }, (_, index) =>
        matchedIndexes.has(index) ? { still_treating: false } : {},
      ),
    },
  };
};
